import {useEffect, useState} from 'react';
import Http from '@assets/services/Http';

/**
 * useFetchApi hook for fetch data from api with url
 *
 * @param url
 * @param defaultData
 * @param presentData
 * @param initLoad
 * @param method
 * @param token
 * @param queryParams
 * @param postData
 * @returns {{fetchApi: ((function(*, null=): Promise<AxiosResponse<any>|*|undefined>)|*), setFetched: (value: (((prevState: boolean) => boolean) | boolean)) => void, data: *[], setData: (value: (((prevState: *[]) => *[]) | *[])) => void, pageInfo: {}, count: number, extraCount: {}, additionalData: {}, loading: boolean, setCount: (value: (((prevState: number) => number) | number)) => void, fetched: boolean}}
 */
export default function useFetchApi({
  url,
  defaultData = [],
  presentData = null,
  initLoad = true,
  method = '',
  token = '',
  queryParams = {},
  postData = {}
}) {
  const [loading, setLoading] = useState(initLoad);
  const [fetched, setFetched] = useState(false);
  const [data, setData] = useState(defaultData);
  const [additionalData, setAdditionalData] = useState({});
  const [pageInfo, setPageInfo] = useState({});
  const [count, setCount] = useState(0);
  async function fetchApi(apiUrl, params = null) {
    try {
      setLoading(true);
      const path = apiUrl || url;
      const resp = await Http({
        url: path,
        method: method,
        token,
        params: {...queryParams, ...params},
        data: postData
      });
      if (resp.hasOwnProperty('additional_data')) {
        setAdditionalData(resp.additional_data);
      }
      if (resp.hasOwnProperty('data')) {
        let actualData = resp.data;

        // Handle Laravel pagination format where data is nested: resp.data.data
        if (resp.data && typeof resp.data === 'object' && resp.data.hasOwnProperty('data')) {
          actualData = resp.data.data;
        }

        const newData = presentData ? presentData(actualData) : actualData;
        setData(Array.isArray(newData) ? newData : {...defaultData, ...newData});
      }
      // Check for Laravel pagination format in both top level and nested data structure
      const paginationData =
        resp.data && typeof resp.data === 'object' && !Array.isArray(resp.data) ? resp.data : resp;

      if (
        paginationData.hasOwnProperty('current_page') &&
        paginationData.hasOwnProperty('per_page') &&
        paginationData.hasOwnProperty('total')
      ) {
        // Handle Laravel native pagination format
        setPageInfo({
          curPage: paginationData.current_page,
          perPage: paginationData.per_page,
          totalItem: paginationData.total
        });
      } else if (
        paginationData.hasOwnProperty('cur_page') &&
        paginationData.hasOwnProperty('per_page') &&
        paginationData.hasOwnProperty('total')
      ) {
        // Handle custom pagination format (from Filterable trait)
        setPageInfo({
          curPage: paginationData.cur_page,
          perPage: paginationData.per_page,
          totalItem: paginationData.total
        });
      }
      return resp;
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  }

  useEffect(() => {
    if (initLoad) {
      fetchApi().then(() => {});
    }
  }, []);

  return {
    fetchApi,
    data,
    additionalData,
    setData,
    pageInfo,
    count,
    setCount,
    loading,
    fetched,
    setFetched
  };
}
